/* eslint-disable react-hooks/exhaustive-deps */
// React
import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useImperativeHandle
} from 'react';
import PropTypes from 'prop-types';

// CYBS-UI
import CybsTextField from '@cybs-ui/components/lib/textfield';
import CybsMenu from '@cybs-ui/components/lib/menu';
import { kebabCase } from '../../utils/StringUtils';

// Internal
import styles from './component.styles';
import { useNamedRef, useWindowSize } from '../../utils/customHooks';
import { placeListboxInTable } from '../../utils/menuAlignment';
import { scrollEntireViewportTo } from '../listbox/utils/scrollToItem';

const keyCode = {
  ALT: 'Alt',
  BACKSPACE: 'Backspace',
  COMMAND: 'Command',
  DELETE: 'Delete',
  DOWN: 'ArrowDown',
  DOWN_IE: 'Down',
  END: 'End',
  ESC_IE: 'Esc',
  ESC: 'Escape',
  HOME: 'Home',
  PAGEDOWN: 'PageDown',
  PAGEUP: 'PageUp',
  ENTER: 'Enter',
  SPACE: ' ',
  TAB: 'Tab',
  UP: 'ArrowUp',
  UP_IE: 'Up',
  RIGHT: 'ArrowRight',
  RIGHT_IE: 'Right',
  LEFT: 'ArrowLeft',
  LEFT_IE: 'Left'
};

const isValidChar = str => str.length === 1 && str.match(/\S/);

// Format the value in the input.
function getPropsInputValue(item, itemsSelectedLabel, type) {
  let newValue = '';
  if (item) {
    if (type === 'nested' && item && item.length >= 1) {
      newValue = item.map(el => el.label).join(' | ');
    } else if (item.length === 1 && item[0].label) {
      newValue = item[0].label;
    } else if (item.length > 1) {
      newValue = `${item.length} ${itemsSelectedLabel}`;
    }
  }
  return newValue;
}

/**
 * @typedef {{
 *  label: string,
 *  value: string,
 *  index?: number,
 *  children?: Array<Item>,
 * }} Item
 * @param {Item} item
 * @param {{
 *  index?: (index: number) => number,
 *  children?: (children: Array<Item>) => Array<Item>,
 * }} updaters
 */
function updateItemFields(item, { index, children }) {
  if (!item) return item;
  const ret = { ...item };
  if (typeof index === 'function' && item.index !== undefined) {
    ret.index = index(item.index);
  }
  if (typeof children === 'function' && item.children !== undefined) {
    ret.children = children(item.children);
  }
  return ret;
}

/**
 * @param {Array<Item>} items
 * @param {string} parentLabel
 * @returns {Array<Item>}
 */
function injectBack(items, parentLabel = '') {
  let id = -1; // pre-incremented
  function inner(currentItems, currentParent) {
    const newItems = currentItems.map(item =>
      updateItemFields(item, {
        children: children => inner(children, item.label)
      })
    );
    if (currentParent) {
      id += 1;
      return [
        {
          label: currentParent,
          value: `--back-${id}`,
          isBack: true
        },
        ...newItems
      ];
    }
    return newItems;
  }
  return inner(items, parentLabel);
}

/**
 * @param {Item} selectedItem
 * @param {number} level
 */
function injectBackIntoSelectedItem(selectedItem, level) {
  return updateItemFields(selectedItem, {
    children: children => injectBack(children, selectedItem.label),
    index: index => index + (level > 0 ? 1 : 0)
  });
}

/**
 * @param {Item} selectedItem
 * @param {number} level
 * @returns {Array<Item>}
 */
function removeBackFromChildren(selectedItem, level) {
  if (selectedItem.isBack) return [];
  return [
    updateItemFields(selectedItem, {
      children: c => c.flatMap(it => removeBackFromChildren(it, level + 1)),
      index: i => i - (level > 0 ? 1 : 0)
    })
  ];
}

const CybsCombobox = React.forwardRef(
  (
    {
      className,
      autofocus,
      dataTest,
      disabled,
      i18n: i18nProp,
      inputProps,
      isFullWidth,
      isInputInvalid,
      // eslint-disable-next-line no-unused-vars
      isOpen,
      items: oldItems,
      onClose,
      onSelect,
      onScrollBottom,
      onBlur,
      optionsTemplate,
      position,
      readOnly,
      required,
      searchKey,
      selectedItems: selectedItemsProp,
      type,
      filterFunction,
      // eslint-disable-next-line no-unused-vars
      validateOnSelection,
      width,
      searchBehavior,
      selectBehavior,
      useNativePlaceholder,
      customCSS,
      lang,
      tableId,
      compactListMode
    },
    ref
  ) => {
    const i18n = {
      ...CybsCombobox.defaultProps.i18n,
      ...i18nProp
    };

    const { width: windowWidth, height: windowHeight } = useWindowSize();
    const [showBackButton, setShowBackButton] = useState(false);

    const items = React.useMemo(
      () => (showBackButton ? injectBack(oldItems) : oldItems),
      [oldItems, showBackButton]
    );
    const selectedItemsInput = React.useMemo(() => {
      const selectedItems = Array.isArray(selectedItemsProp)
        ? selectedItemsProp.slice()
        : [selectedItemsProp];
      return showBackButton
        ? injectBack(selectedItemsProp)
        : // we have to slice because navigateFocus mutates this value
          selectedItems;
    }, [selectedItemsProp, showBackButton]);

    // Hooks
    // Centinel variable to make sure that the onblur is not trigger on load
    const [avoidBlurOnLoad, setAvoidBlurOnLoad] = useState(true);
    // Track if the component is active (for the activation of the glasspane)
    const [isActiveComponent, setActiveComponent] = useState(false);
    // Track Dropdown visibility. Track if isOpen changes.
    const [isDropdownOpen, toggleDropdown] = useState(false); // According to a11y spec, dropdown never starts open.
    // Keep track of the selected item(s).
    const [selectedItems, _setSelection] = useState(selectedItemsInput);
    // Keep track of the value manually typed by the user
    const [inputValue, setInputValue] = useState({
      value: getPropsInputValue(
        selectedItemsInput,
        i18n.itemsSelectedLabel,
        type
      ),
      isMultiValue: false
    });

    const [isValueVisible, showInputValue] = useState(true);
    const [isNavigating, setNavigationMode] = useState(false);
    const [focusedItemObj, setFocusedItem] = useState({ index: -1, value: '' });
    const [itemsList, setItemsList] = useState(items);
    const [focusableItemObject, setfocusableItemObjects] = useState(items);
    const [screenReaderMessage, setScreenReaderMessage] = useState('');
    const [openNestedDropdown, setNestedDropdown] = useState('');
    const [scrollBottom, setScrollBottom] = useState(false);

    // Refs
    const menuRef = useNamedRef('menu', null);
    const inputRef = useNamedRef('input', null);
    const comboRef = useNamedRef('combo', null);
    const selectedRef = useNamedRef('selected', null);

    /**
     * Recompute `focusableItemObjects` to equal the menu containing the
     * selected item.
     *
     * --- WARNING ----
     * If the combobox has categories, this will cause the arrow keys to
     * select the headers and not its children, which is an error
     * (see CYBSUI-2986). Hopefully categories and nested comboboxes are
     * rarely or never used together.
     *
     * @param {Array<Item>} selected selected items list
     */
    function recomputeFocusableItemObjects(selected = selectedItems) {
      // The last value in selectedItems is the currently selected item,
      //  so the menu that contains it is the second-to-last value in
      //  selected items.
      // Note that when a menu is open but nothing in it is selected,
      //  selectedItems still contains {} as its last value, so the above
      //  invariant is maintained.
      // Alternatively, if selectedItems is only 0 or 1 level deep, then
      //  we're at the top-level listbox.
      if (selected.length < 2) {
        setfocusableItemObjects(items);
        return;
      }

      // Easy path: the parent listbox children were provided so we don't have
      //  to recompute them.
      const parentListbox = selected[selected.length - 2].children;
      if (parentListbox) {
        setfocusableItemObjects(parentListbox);
        return;
      }

      // Unhappy path: we need to dig through items because the children weren't
      //  provided.
      // This is mostly provided for backcompat, and shouldnt be triggered in
      //  most situations.
      /** @type Array<Item> */
      let currentItems = items;
      selected.slice(0, -1).forEach(selectedItem => {
        const { value } = selectedItem;
        const found = currentItems.find(item => item.value === value);
        if (found && found.children) {
          currentItems = found.children;
        } else {
          console.error(
            'invalid value in selectedItems provided',
            selectedItem
          );
        }
      });
      setfocusableItemObjects(currentItems);
    }

    // Ensure the combobox remains on-screen while the page reflows due to resize
    useLayoutEffect(() => {
      // If in Table, use separate logic
      if (tableId && menuRef?.current) {
        placeListboxInTable(tableId);
        // Commented next line as util does not support scrollable tables.
        // scrollEntireViewportTo(menuRef.current); 
        return;
      }
      if (isDropdownOpen && document.activeElement === inputRef.current) {
        const { top, bottom, left, right } =
          inputRef.current.getBoundingClientRect();

        // This is approximate because it doesn't understand overflow, but it
        //  should work when the combobox leaves the screen due to reflow at
        //  least.
        const isApproxVisible =
          top >= 0 &&
          left >= 0 &&
          bottom <= windowHeight &&
          right <= windowWidth;

        if (!isApproxVisible) {
          const alignToTop = position?.vertical !== 'top';
          inputRef.current.scrollIntoView(alignToTop);
        }
      }
    }, [windowWidth, windowHeight, isDropdownOpen]);

    // Estimate whether the listbox will fit on the screen
    const doesListboxFitOnScreen = () => {
      const bounds = inputRef.current.getBoundingClientRect();
      // Assumes all listboxes are 3 levels deep
      // Number found by looking at the rendered listbox, not from code.
      const neededSpace = 705;
      const remainingSpace =
        position?.horizontal === 'right'
          ? bounds.right - 56 // 56px = nav aside width
          : windowWidth - bounds.left;
      return remainingSpace >= neededSpace;
    };

    // Show the back button when the listbox stops fitting on the screen
    useEffect(() => {
      if (type === 'nested' && isDropdownOpen) {
        setShowBackButton((!doesListboxFitOnScreen() || compactListMode === true));
      }
    }, [isDropdownOpen, windowWidth]);

    // Fix state when back button is added or removed
    useEffect(() => {
      setItemsList(items);
      if (type === 'nested') {
        _setSelection(selected => {
          const newSelected = showBackButton
            ? selected.map(injectBackIntoSelectedItem)
            : selected.flatMap(removeBackFromChildren);
          recomputeFocusableItemObjects(newSelected);
          return newSelected;
        });
      }
    }, [showBackButton]);

    // Keep the currently focused index in sync with the value whenever
    //  focusableItemObject changes. This changes for one of two reasons:
    //   1. the user just did a search
    //   2. the user navigated to a new level.
    // In both situations, the following invariant should hold:
    //   focusableItemObject[focusedItemObj.index].value ===
    //   focusedItemObj.value
    useEffect(() => {
      if (focusedItemObj.value) {
        const foundIdx = focusableItemObject.findIndex(
          item => item.value === focusedItemObj.value
        );
        setFocusedItem({
          ...focusedItemObj,
          index: foundIdx
        });
      }
    }, [focusableItemObject]);

    const checkSearchBehaviour = () => {
      if (searchBehavior) {
        onScrollBottom();
      }
    };
    function setSelection(value) {
      selectedRef.current = value;

      // TODO: Value should be an array, something is causing it to be an object sometimes
      if (Array.isArray(value)) {
        _setSelection(value);
      } else {
        _setSelection([value]);
      }
    }
    if (ref) {
      useImperativeHandle(
        ref,
        () => ({
          // Allow implementation to focus on imput.
          focus() {
            if (inputRef.current) inputRef.current.focus();
          },
          // Get the value of the input text field.
          get value() {
            return inputRef.current.value;
          },
          // return a copy
          get selected() {
            return selectedItems.slice();
          }
        }),
        [selectedItems, inputRef]
      );
    }

    function handleSelect(event, selected) {
      if (typeof onSelect !== 'function') return;
      if (showBackButton) {
        selected = selected.flatMap(removeBackFromChildren);
      }
      onSelect(event, selected);
    }

    function handleBlur(event, invalid, selected) {
      const onBlurFunction = onBlur || inputProps.onInputBlur;
      if (typeof onBlurFunction !== 'function') return;
      if (showBackButton) {
        selected = selected.flatMap(removeBackFromChildren);
      }
      onBlurFunction(event, invalid, selected);
    }

    function getSingleInputValue(item) {
      if (item) {
        setInputValue({
          value: typeof item === 'object' ? item.label : item,
          isMultiValue: false
        });
      }
    }

    function getNestedInputValue(item, allSelectedItems) {
      let newValue = '';
      if (allSelectedItems && allSelectedItems.length > 1) {
        newValue = allSelectedItems.map(el => el.label).join(' | ');
      } else {
        newValue = item.label;
      }
      setInputValue({ value: newValue, isMultiValue: false });
    }

    function getMultiInputValue(item, allSelectedItems) {
      let newValue = item;

      let isMultiValue = false;
      if (allSelectedItems) {
        if (allSelectedItems.length === 0) {
          newValue = '';
          isMultiValue = false;
        } else if (allSelectedItems.length === 1) {
          if (allSelectedItems.find(i => i.value === item.value)) {
            newValue = item.label;
            isMultiValue = true;
          } else {
            newValue = allSelectedItems[0].label;
            isMultiValue = true;
          }
        } else if (allSelectedItems.length > 1) {
          newValue = `${allSelectedItems.length} ${i18n.itemsSelectedLabel}`;
          isMultiValue = true;
        }
      }
      setInputValue({ value: newValue, isMultiValue });
    }

    // Format the value in the input.
    function formatInputValue(item, selected) {
      switch (type) {
        case 'multi':
          getMultiInputValue(item, selected);
          break;
        case 'nested':
          getNestedInputValue(item, selected);
          break;
        case 'single':
        default:
          getSingleInputValue(item);
      }
    }

    useEffect(() => {
      if (autofocus) {
        setActiveComponent(true);
      }
    }, [1]);

    function handleDocumentClick(e) {
      if (
        comboRef.current.contains(e.target) ||
        menuRef?.current?.contains(e.target)
      ) {
        return;
      }

      // eslint-disable-next-line no-use-before-define
      handleInputBlur(e, true);
    }
    function cleanupEvent() {
      document.removeEventListener('mousedown', handleDocumentClick);
    }
    useEffect(() => {
      if (!avoidBlurOnLoad) {
        if (isActiveComponent) {
          if (inputRef && inputRef.current) {
            inputRef.current.focus();
          }
          document.addEventListener('mousedown', handleDocumentClick);
        } else {
          const invalid =
            inputRef &&
            inputRef.current &&
            // eslint-disable-next-line no-use-before-define
            validateInput(inputRef.current.value);
          const fakeEvent = { target: inputRef.current };
          handleBlur(fakeEvent, invalid, selectedItems);
        }
      } else {
        setAvoidBlurOnLoad(false);
      }

      return cleanupEvent;
    }, [isActiveComponent]);

    // If the dropdown closes, and we are the active component, we need to make sure the focus stays on the input.
    useEffect(() => {
      if (isActiveComponent && inputRef && inputRef.current) {
        // Reset the focus on the input when the dropdown closes.
        inputRef.current.focus();
      }
      if (!isDropdownOpen && typeof onClose === 'function') {
        onClose();
      }
    }, [isDropdownOpen]);

    /**
     * Toggle open state of the menu.
     * Side effects: Changes inputNavigation mode and/or showInputValue
     * @param open boolean
     * @return void
     *
     */
    function setDropdownOpenTo(open) {
      if (!isDropdownOpen) {
        if (type === 'multi') {
          let newValue = inputValue.value;
          let isMultiValue = false;
          if (selectedItems) {
            if (selectedItems.length === 0) {
              newValue = '';
            } else if (selectedItems.length === 1) {
              newValue = selectedItems[0].label;
            } else if (selectedItems.length > 1) {
              newValue = `${selectedItems.length} ${i18n.itemsSelectedLabel}`;
              isMultiValue = true;
            }
            setInputValue({ value: newValue, isMultiValue });
          }
        }
      }
      toggleDropdown(open);
    }

    function navigateFocus(shouldUpdateSelection, item, index, open) {
      if (shouldUpdateSelection) {
        if (type === 'nested') {
          // TODO: Consider eliminating this mutation. This previously mutated
          //  the selectedItems prop, although this was bypass via a slice.
          //  Still, React state probably shouldn't be mutated like this.
          selectedItems.splice(selectedItems.length - 1, 1, { ...item, index });
          setSelection(
            selectedItems && selectedItems.length === 0
              ? [{ ...item, index }]
              : [...selectedItems]
          );
          formatInputValue(item, selectedItems);
        } else {
          setSelection([item]);
          formatInputValue(item);
        }
      }
      setFocusedItem({
        index,
        value: typeof item === 'object' ? item.value : item
      });

      if (open === false) {
        if (type === 'nested') {
          setfocusableItemObjects(items);
          setNestedDropdown('');
        }

        setDropdownOpenTo(open);
      }
    }

    function handleKeyboardNavigation(index) {
      const item = focusableItemObject[index];
      const { categoryName } = item;

      if (categoryName) {
        setScreenReaderMessage(`Entering category: ${categoryName}`);
      }
      if (item.value !== 'no_results') {
        if (type === 'multi') {
          navigateFocus(false, item, index);
        } else if (type === 'single') {
          navigateFocus(true, item, index);
        } else if (type === 'nested') {
          navigateFocus(true, item, index);
        }
      }
    }

    function clearFocus(resetList) {
      if (resetList) {
        setSelection([]);
      }

      setFocusedItem({ index: -1, value: '' });
    }

    const noResultsObj = { label: i18n.noResultsLabel, value: 'no_results' };
    function setNoSelection(value) {
      setSelection([noResultsObj]);
      setItemsList([noResultsObj]);
      formatInputValue(value);
      setFocusedItem({ index: -1, value: '' });
    }

    useEffect(() => {
      if (selectedItemsInput) {
        if (selectedItemsInput.length > 0) {
          let tempSelectedItemsProp = selectedItemsInput;
          if (type === 'nested') {
            // add index to the selected items
            let listOfItems = items;
            tempSelectedItemsProp = selectedItemsInput.map((item, idx) => {
              const index = listOfItems?.findIndex(
                elem => elem.value === item.value
              );
              listOfItems = listOfItems[index]?.children;
              return {
                ...item,
                level: idx,
                index
              };
            });
          }

          if (type === 'single') {
            if (itemsList && itemsList.length > 0) {
              setFocusedItem({
                index: itemsList.findIndex(
                  item => item.value === selectedItemsInput[0].value
                ),
                value: selectedItemsInput[0].value
              });
            }
          }

          if (!isDropdownOpen || !isValueVisible) {
            setSelection(tempSelectedItemsProp);
            setInputValue({
              value: getPropsInputValue(
                tempSelectedItemsProp,
                i18n.itemsSelectedLabel,
                type
              ),
              isMultiValue: type === 'multi' && selectedItemsInput.length > 1
            });
          }
        } else if (selectedItemsInput.length === 0) {
          if (!isDropdownOpen || !isValueVisible) {
            setSelection(selectedItemsInput);
            setInputValue({
              value: getPropsInputValue(
                selectedItemsInput,
                i18n.itemsSelectedLabel,
                type
              ),
              isMultiValue: type === 'multi' && selectedItemsInput.length > 1
            });
          }
        }
      }
    }, [selectedItemsInput]);

    // Reset values when the dropdown opens or closes
    useEffect(() => {
      if (!isDropdownOpen) {
        setNavigationMode(false);
        // Set isInputInvalid to true on blur (meaning, when the dropdown closes)
        showInputValue(true);
        // Reset the initial list when the dropdown closes.
        if (
          (selectedItems && selectedItems.length === 0) ||
          (selectedItems &&
            selectedItems[0] &&
            selectedItems[0].value !== 'no_results')
        ) {
          setItemsList(items);
        }

        if (type === 'multi') {
          clearFocus();
        }
      }

      if (type === 'multi') {
        if (isDropdownOpen) {
          // Hide the input value when the dropdown opens and when using arrows key to navigate in the dropdown.
          if (isNavigating) {
            showInputValue(false);
          } else {
            showInputValue(true);
          }
        }
      }
    }, [items, isDropdownOpen]);

    // List of all focusable list items (not disabled).
    useEffect(() => {
      let focusableObjects =
        type === 'nested' ? focusableItemObject : itemsList;
      if (focusableObjects) {
        const categories = focusableObjects.filter(item => item.categories);
        const children = focusableObjects.filter(item => item.children);

        // Return a flat array when there is categories or nested children.
        if (categories && categories.length > 0) {
          focusableObjects = categories
            .flatMap(cat =>
              cat.categories.map(item => ({ ...item, categoryName: cat.value }))
            )
            .filter(i => !i.disabled);
        } else if (children && children.length > 0) {
          const match = children.findIndex(
            child => child.children[0].value === openNestedDropdown
          );
          if (match > -1) {
            focusableObjects = children[match].children
              .flat() // not sure why this is here, but i'm not removing it
              .filter(i => !i.disabled);
          } else {
            focusableObjects = focusableObjects
              .flat() // ditto
              .filter(i => !i.disabled);
          }
        } else {
          focusableObjects = focusableObjects.filter(i => !i.disabled);
        }
        setfocusableItemObjects(focusableObjects);
      }
    }, [itemsList, openNestedDropdown]);

    const onClickBack = event => {
      let newSelectedItems = selectedItems;
      if (event) {
        if (selectBehavior) {
          event.preventDefault();
          return;
        }
        if (type === 'single') {
          newSelectedItems = [];
          setSelection(newSelectedItems);
          setFocusedItem({ index: -1, value: '' });
        } else if (
          type === 'nested' &&
          selectedItems.length > 1 &&
          isDropdownOpen
        ) {
          // eslint-disable-next-line no-use-before-define
          newSelectedItems = onEscPressedNested();
        }
        if (inputProps && typeof inputProps.onInputKeyDown === 'function') {
          inputProps.onInputKeyDown(event);
        }
      }
    };

    // Click event on a list item.
    function handleItemClick(event, item, allSelectedItems) {
      if (event) {
        event.stopPropagation();
        inputRef.current.focus();

        if (allSelectedItems) {
          const includes =
            selectedItems && selectedItems.some(i => i.value === item.value);
          if (type === 'multi') {
            const index = focusableItemObject.findIndex(
              el => el.value === item.value
            );

            let newSelectedItems;
            if (includes) {
              newSelectedItems = [
                ...selectedItems.filter(i => i.value !== item.value)
              ];
            } else {
              newSelectedItems = selectedItems.concat([item]);
            }
            setSelection(newSelectedItems);
            setFocusedItem({ index, value: item.value });
            formatInputValue(item, newSelectedItems);
            showInputValue(false);
          } else if (type === 'nested') {
            if (item.isBack) {
              onClickBack(event);
            } else {
              // Here, we have to set the focused object, the focusable items
              // array, and the selected items array.
              //
              // The relevant state is as follows:
              //   - the focused object (focusableItemObject) must be set to the
              //     item currently "hovered over" by the user via the arrow
              //     keys or clicked via the mouse
              //   - the selected items array (selectedItems) contains the path
              //     to the focused object (i.e. the parent options and the
              //     focused option)
              //   - the focusable items array (focusableItemObject) is an array
              //     containing the options in the current dropdown
              //   - `openNestedDropdown` should be set to the first child of
              //     the current dropdown
              //
              // The easy use case is that the user clicked on an item under the
              // currently focused item.
              //
              // The difficult use case is that the user instead clicked on one
              // of the items under another dropdown. In that case, we have to
              // check backwards for the item the user clicked.
              //
              // Please see `onEnterPressedNested`, as it does something very
              // similar, only for keyboard events rather than mouse events.
              // That these two parts of the code handle the same thing may
              // constitute a violation of the single-responsibility principle
              // or might indicate some kind of coupling, so:
              //   TODO: Investigate and, if applicable, remove coupling to `onEnterPressedNested`

              // find the current item in the selected items list or the items
              // list, in order to find its children & parent & depth
              const findItemWithValue = () => {
                // We prepend the itemsList to the path, since otherwise clicking
                // a top-level item causes a lookup failure (as there is no parent
                // to our item in the selected items list).
                // Parents are only needed for the children, so thats the only
                // needed field.
                const path = [{ children: itemsList }, ...selectedItems];
                // Scan backwards through the path to find the current item
                // and its parent. Each step in the path represents one of the
                // dropdowns (higher index = higher depth).
                for (let depth = path.length - 1; depth >= 0; --depth) {
                  // the parent item that we are currently looking at, not
                  // necessarily the parent to `item`
                  const currentParent = path[depth];
                  const itemIndex = currentParent.children?.findIndex(
                    child => child.value === item.value
                  );
                  const found = currentParent.children?.[itemIndex];

                  if (found) {
                    return {
                      depth,
                      parent: currentParent,
                      // found should have an index to match rest of component
                      found: { index: itemIndex, ...found }
                    };
                  }
                }

                return { depth: -1, parent: null, found: null };
              };

              const { depth, parent, found } = findItemWithValue();

              if (!found) {
                console.error('Internal error, did not find clicked item.');
                return;
              }

              const newSelectedItems = [
                ...selectedItems.slice(0, depth),
                found
              ];

              if (item.hasNestedDropdown && found.children?.[0]) {
                newSelectedItems.push({ label: '', value: '' });
                setFocusedItem({
                  index: -1,
                  value: found.value
                });
                setNestedDropdown(found.children[0].value);
                setfocusableItemObjects(found.children);
              } else {
                setFocusedItem({
                  index: found.index,
                  value: found.value
                });
                // ternary below doesn't seem immediately required, but
                // is done for consistency with the keyboard navigation code
                setNestedDropdown(depth ? parent.children[0].value : '');
                setfocusableItemObjects(parent.children);
                setDropdownOpenTo(false);
              }
              setSelection(newSelectedItems);
              formatInputValue(found, newSelectedItems);
            }
          } else {
            const index = focusableItemObject.findIndex(
              el => el.value === item.value
            );
            setSelection(allSelectedItems);
            setFocusedItem({ index, value: '' });
            formatInputValue(
              optionsTemplate ? item.label : item,
              allSelectedItems
            );
            setDropdownOpenTo(false);
          }
        }
      }

      let previous = itemsList;
      const aux = allSelectedItems.map(element => {
        if (element.index > -1 && previous?.length) {
          const newValue = previous[element.index];
          previous = previous[element.index].children;
          return {
            ...element,
            ...newValue
          };
        }
        return element;
      });
      handleSelect(event, aux);
    }

    // Blur event on the input element.
    function validateInput(value) {
      let invalid = isInputInvalid;
      // Set invalid to true if the input value does not match any item in the list but ignored that rule when value is 'x items selected'
      if (
        (isInputInvalid &&
          itemsList &&
          itemsList.length === 1 &&
          itemsList[0].value === 'no_results') ||
        (!inputValue.isMultiValue &&
          value &&
          type !== 'nested' &&
          (optionsTemplate?.validationTemplate
            ? optionsTemplate.validationTemplate(focusableItemObject, value)
            : focusableItemObject?.findIndex(
                item => item.label?.toLowerCase() === value.toLowerCase()
              ) === -1))
      ) {
        invalid = true;
      } else {
        invalid = false;
      }
      return invalid;
    }

    // Handle the real input blur....
    function handleInputBlur(event, force) {
      if (isActiveComponent && !force) {
        return;
      }

      setDropdownOpenTo(false);
      setActiveComponent(false);

      if (type === 'single') {
        const indexItem = itemsList?.findIndex(
          item =>
            item.label?.toLowerCase() ===
            inputRef?.current?.value?.toLowerCase()
        );

        // edge case: we retrigger the onselect if the field was invalid but it had a correct value
        if (
          indexItem >= 0 &&
          ((event.target === inputRef.current &&
            selectedRef?.current[0]?.value !== selectedItemsInput[0]?.value) ||
            inputRef?.current?.value?.toLowerCase() !==
              selectedRef?.current[0]?.label.toLowerCase() ||
            isInputInvalid)
        ) {
          if (inputRef?.current?.value !== itemsList[indexItem].label) {
            inputRef.current.value = itemsList[indexItem].label;
          }
          setSelection([itemsList[indexItem]]);
          handleSelect(event, [itemsList[indexItem]]);
        }
      }
      if (type === 'nested') {
        const inputVal = inputRef?.current?.value?.toLowerCase();
        const matchingItem = itemsList?.find(
          item => item.label?.toLowerCase() === inputVal
        );

        // edge case: we retrigger the onselect if the field was invalid but it had a correct value
        // and was not a nested field
        if (
          matchingItem &&
          !matchingItem.children &&
          ((event.target === inputRef.current &&
            selectedRef.current[0]?.value !== selectedItemsInput[0]?.value) ||
            inputRef?.current?.value?.toLowerCase() !==
              selectedRef?.current[0]?.label.toLowerCase() ||
            isInputInvalid)
        ) {
          if (inputRef?.current?.value !== matchingItem.label) {
            inputRef.current.value = matchingItem.label;
          }
          setSelection([matchingItem]);
          handleSelect(event, [matchingItem]);
          return;
        }

        // matchingItem is falsey
        if (selectedRef.current) {
          const refLen = selectedRef.current.length;
          if (refLen) {
            if (
              selectedRef.current[refLen - 1]?.value === undefined ||
              Array.isArray(selectedRef.current[refLen - 1]?.children)
            ) {
              setNestedDropdown('');
              handleSelect(event, []);
              setSelection([{}]);
              selectedRef.current = [null];
            }
            return;
          }
        }

        if (
          selectedItems &&
          selectedItems[selectedItems.length - 1] &&
          selectedItems[selectedItems.length - 1].children
        ) {
          setSelection([{}]);
          formatInputValue({}, [{}]);
          setFocusedItem({ index: -1, value: '' });
          setfocusableItemObjects(items);
          setNestedDropdown('');
          handleSelect(event, []);
        }
      }
    }

    const onEscPressedNested = () => {
      const newFocusedItem =
        selectedItems.length > 2
          ? selectedItems?.[0]?.children?.[0]?.value
          : selectedItems?.[0]?.value;
      // TODO: Remove optional chaining when mouse/keyboard state issue solved?
      if (selectedItems.length > 1) {
        const nestedSelectedItems = [
          ...selectedItems.splice(0, selectedItems.length - 1)
        ];
        setSelection(nestedSelectedItems);
        formatInputValue(
          nestedSelectedItems[nestedSelectedItems.length - 1],
          nestedSelectedItems
        );
        setFocusedItem({
          index: nestedSelectedItems[nestedSelectedItems.length - 1].index,
          value: ''
        });
        setfocusableItemObjects(items);
        setNestedDropdown(newFocusedItem);
        return nestedSelectedItems;
      }
      return [];
    };

    /**
     * This function is only used for nested comboboxes. Select a dropdown
     * item that has nested dropdown options.
     *
     * This is also only used for keyboard events.
     * @see handleItemClick for mouse event handling of the same thing, and to
     *                      see documentation for state used herein.
     */
    const onEnterPressedNested = currentlyFocusedItem => {
      let nestedSelectedItems = [];

      if (
        currentlyFocusedItem &&
        currentlyFocusedItem.children &&
        currentlyFocusedItem.children[0]
      ) {
        nestedSelectedItems = [
          ...selectedItems,
          currentlyFocusedItem.children[0]
        ];
        setFocusedItem({
          index: 0,
          value: currentlyFocusedItem.children[0].value
        });
        setSelection(nestedSelectedItems);
        formatInputValue(currentlyFocusedItem, nestedSelectedItems);
        setNestedDropdown(currentlyFocusedItem.children[0].value);
      }

      return nestedSelectedItems;
    };

    // Key down event on the input element.
    const handleInputKeyDown = event => {
      setScreenReaderMessage('');

      if (disabled || readOnly) {
        return;
      }
      const count = focusableItemObject?.length;
      let newSelectedItems = selectedItems;
      if (event && focusableItemObject) {
        let newIndex = focusedItemObj.index;
        let currentlyFocusedItem = focusableItemObject[focusedItemObj.index];
        if (
          (isValidChar(event.key) ||
            event.key === keyCode.DELETE ||
            event.key === keyCode.BACKSPACE) &&
          selectBehavior
        ) {
          event.preventDefault();
        } else if (isValidChar(event.key)) {
          // Update input with the valid character and display new input.
          setNavigationMode(false);
          setInputValue({ value: event.target.value, isMultiValue: false });
          showInputValue(true);
        } else if (event.altKey && event.key === keyCode.UP) {
          // Prevent cursor from moving to the beginning of the input value.
          event.preventDefault();
          // If the error state was previously set to true on blur, set it back to false.
          setDropdownOpenTo(false);
        } else if (
          event.key === keyCode.TAB ||
          event.key === keyCode.ESC ||
          event.key === keyCode.ESC_IE
        ) {
          setNavigationMode(false); // disable navigation.
          if (isDropdownOpen) {
            if (event.key === keyCode.ESC || event.key === keyCode.ESC_IE) {
              event.nativeEvent.stopImmediatePropagation();
            }
            if (
              type === 'nested' &&
              selectedItems.length > 1 &&
              (event.key === keyCode.ESC || event.key === keyCode.ESC_IE)
            ) {
              newSelectedItems = onEscPressedNested();
            } else {
              // close the dropdown and return to the input box.
              event.preventDefault();
              setDropdownOpenTo(false);
              if (
                type === 'nested' &&
                selectedItems &&
                selectedItems[selectedItems.length - 1] &&
                selectedItems[selectedItems.length - 1].children
              ) {
                setSelection([{}]);
                formatInputValue(items[0], [{}]);
                setFocusedItem({ index: -1, value: '' });
                setfocusableItemObjects(items);
                setNestedDropdown('');

                handleSelect(event, []);
                inputRef.current.value = [null];
                selectedRef.current = [null];
              }
            }
          } else {
            handleInputBlur(event, true);
          }
        } else if (event.key === keyCode.UP || event.key === keyCode.UP_IE) {
          // Set navigating mode to true to notify that the user is interacting with the listbox using arrow keys.
          setNavigationMode(true);
          event.preventDefault();

          if (focusedItemObj.index === -1) {
            newIndex = -1;
          } else {
            newIndex =
              focusedItemObj.index - 1 < 0 ? 0 : focusedItemObj.index - 1;
          }

          if (focusableItemObject[newIndex] && isDropdownOpen) {
            if (focusableItemObject[newIndex]) {
              handleKeyboardNavigation(newIndex);
            }
          }
        } else if (
          event.key === keyCode.DOWN ||
          event.key === keyCode.DOWN_IE
        ) {
          event.preventDefault();
          if (type === 'multi' || type === 'nested') {
            setNavigationMode(true);
          }
          if (isDropdownOpen === false) {
            setDropdownOpenTo(true);
            if (type === 'nested') {
              recomputeFocusableItemObjects();
            }
          } else {
            if (type === 'single') {
              setNavigationMode(true);
            }
            newIndex =
              focusedItemObj.index + 1 < count - 1
                ? focusedItemObj.index + 1
                : count - 1;
            if (focusableItemObject[newIndex]) {
              handleKeyboardNavigation(newIndex);
            }
          }
        } else if (
          event.key === keyCode.RIGHT ||
          event.key === keyCode.RIGHT_IE
        ) {
          if (selectBehavior) {
            event.preventDefault();
            return;
          }
          if (type === 'single') {
            newSelectedItems = [];
            setSelection(newSelectedItems);
            setFocusedItem({ index: -1, value: '' });
          } else if (type === 'nested' && isDropdownOpen) {
            newSelectedItems = onEnterPressedNested(currentlyFocusedItem);
          }
        } else if (
          event.key === keyCode.LEFT ||
          event.key === keyCode.LEFT_IE
        ) {
          if (selectBehavior) {
            event.preventDefault();
            return;
          }
          if (type === 'single') {
            newSelectedItems = [];
            setSelection(newSelectedItems);
            setFocusedItem({ index: -1, value: '' });
          } else if (
            type === 'nested' &&
            selectedItems.length > 1 &&
            isDropdownOpen
          ) {
            newSelectedItems = onEscPressedNested();
          }
        } else if (event.key === keyCode.SPACE) {
          if (selectBehavior) {
            event.preventDefault();
          }
          if (selectBehavior && !isDropdownOpen) {
            setDropdownOpenTo(true);
            return;
          }

          if (isNavigating) {
            setNavigationMode(true);
          } else {
            setNavigationMode(false);
            setDropdownOpenTo(true);
          }

          newIndex = focusedItemObj.index;
          if (focusableItemObject[newIndex]) {
            currentlyFocusedItem = focusableItemObject[newIndex];

            if (isInputInvalid || currentlyFocusedItem.value === 'no_results') {
              setDropdownOpenTo(false);
              setNoSelection(event.target.value);
            }

            const includes = selectedItems.some(
              i => i.value === currentlyFocusedItem.value
            );

            if (type === 'multi') {
              event.preventDefault();
              let multiSelectedItems;
              if (includes) {
                multiSelectedItems = [
                  ...selectedItems.filter(
                    i => i.value !== currentlyFocusedItem.value
                  )
                ];
                newSelectedItems = multiSelectedItems;
                setScreenReaderMessage(
                  `${currentlyFocusedItem.label} unchecked`
                );
              } else {
                multiSelectedItems = selectedItems.concat([
                  currentlyFocusedItem
                ]);
                newSelectedItems = multiSelectedItems;
                setScreenReaderMessage(`${currentlyFocusedItem.label} checked`);
              }
              if (isNavigating && !isValueVisible) {
                setSelection(multiSelectedItems || []);
                formatInputValue(currentlyFocusedItem, multiSelectedItems);
                showInputValue(false);
                handleSelect(event, multiSelectedItems);
              } else {
                setSelection(multiSelectedItems || []);
                formatInputValue(event.target.value);
                showInputValue(true);
                handleSelect(event, multiSelectedItems);
              }
            } else {
              navigateFocus(true, currentlyFocusedItem, newIndex);
              handleSelect(event, [currentlyFocusedItem]);
            }
          }
        } else if (event.key === keyCode.ENTER) {
          if (newSelectedItems[newSelectedItems.length - 1]?.isBack) {
            onClickBack(event);
            return;
          }
          event.preventDefault();
          if (!isDropdownOpen) return;
          if (
            type === 'nested' &&
            currentlyFocusedItem &&
            currentlyFocusedItem.children &&
            currentlyFocusedItem.children[0]
          ) {
            onEnterPressedNested(currentlyFocusedItem);
            return;
          }
          setDropdownOpenTo(true);
          if (type === 'multi' || type === 'nested') {
            if (focusedItemObj.index > -1) {
              setNavigationMode(true);
            } else {
              setNavigationMode(false);
              return;
            }
          }

          if (focusedItemObj.index < 0) return;
          newIndex = focusedItemObj.index;
          if (focusableItemObject[newIndex]) {
            currentlyFocusedItem = focusableItemObject[newIndex];

            const includes = selectedItems.some(
              i => i.value === currentlyFocusedItem.value
            );
            if (isInputInvalid || currentlyFocusedItem.value === 'no_results') {
              setDropdownOpenTo(false);
              setNoSelection(event.target.value);
            }
            if (type === 'multi') {
              let multiSelectedItems;
              if (includes) {
                multiSelectedItems = [
                  ...selectedItems.filter(
                    i => i.value !== currentlyFocusedItem.value
                  )
                ];
                newSelectedItems = multiSelectedItems;
                setScreenReaderMessage(
                  `${currentlyFocusedItem.label} unchecked`
                );
              } else {
                multiSelectedItems = selectedItems.concat([
                  currentlyFocusedItem
                ]);
                newSelectedItems = multiSelectedItems;
                setScreenReaderMessage(`${currentlyFocusedItem.label} checked`);
              }
              if (isNavigating) {
                setSelection(multiSelectedItems);
                formatInputValue(currentlyFocusedItem, multiSelectedItems);
                showInputValue(false);
                handleSelect(event, multiSelectedItems);
              }
              if (event.target.value !== '') {
                formatInputValue(event.target.value);
                showInputValue(true);
              }
            } else if (type === 'single') {
              navigateFocus(true, currentlyFocusedItem, newIndex, false);
              handleSelect(event, [currentlyFocusedItem]);
            } else if (type === 'nested') {
              navigateFocus(true, currentlyFocusedItem, newIndex, false);
              handleSelect(event, selectedItems);
            }
          } else {
            setDropdownOpenTo(true);
          }
        } else if (event.key === keyCode.END) {
          event.preventDefault();
          setNavigationMode(true);

          newIndex = count - 1;
          if (focusableItemObject[newIndex]) {
            handleKeyboardNavigation(newIndex);
          }
        } else if (event.key === keyCode.HOME) {
          event.preventDefault();
          setNavigationMode(true);

          newIndex = 0;
          if (focusableItemObject[newIndex]) {
            handleKeyboardNavigation(newIndex);
          }
        }
      }

      if (inputProps && typeof inputProps.onInputKeyDown === 'function') {
        inputProps.onInputKeyDown(event);
      }
    };

    // Click event on the button in the input.
    function handleInputClick(event) {
      // this should already happen.
      event.preventDefault();
      event.stopPropagation();
      if (!isActiveComponent) setActiveComponent(true);
      if (disabled || readOnly) {
        return;
      }
      setNavigationMode(true);
      if (inputProps && typeof inputProps.onInputClick === 'function') {
        inputProps.onInputClick(event);
      }
      setDropdownOpenTo(!isDropdownOpen);

      if (type === 'nested') {
        // This ensures that when the user selects an item in a nested combobox,
        // its children become focused.
        //
        // Without this, if the user selects a child item, blurs the input,
        // and then presses down, the combobox will select the item under the
        // parent instead of under the child.
        recomputeFocusableItemObjects();
      }
    }

    // Focus event on the input.
    function handleInputFocus(event) {
      if (searchBehavior) {
        setScreenReaderMessage(i18n.searchBehavior);
      }
      setActiveComponent(true);
      if (inputProps && typeof inputProps.onInputFocus === 'function') {
        inputProps.onInputFocus(event);
      }
    }

    // Use the input to search a list item.
    function handleInputSearch(target) {
      let newList = items;
      if (typeof filterFunction === 'function') {
        const customMatchedItems = filterFunction(target, items);
        if (customMatchedItems && customMatchedItems.length === 0) {
          newList = [{ label: i18n.noResultsLabel, value: 'no_results' }];
          setItemsList(newList);
          setfocusableItemObjects(newList);
        } else {
          newList = customMatchedItems;
          setItemsList(customMatchedItems);
          setfocusableItemObjects(customMatchedItems);
        }
      } else if (target) {
        const subMatches = [];
        let categoryMatchedItems;
        const lowerCaseTarget = target.toLowerCase();
        const matchedItems = items?.filter(item => {
          let temp;
          const arraySearchKey =
            typeof searchKey === 'string' ? [searchKey] : searchKey;
          arraySearchKey.filter(searchKeyItem => {
            const itemKey = item[searchKeyItem];
            if (item && itemKey) {
              const lowerCaseItemKey = itemKey.toLowerCase();
              const matchBool = lowerCaseItemKey.startsWith(lowerCaseTarget);
              if (matchBool && !item.categories) {
                temp = item;
              }
              // Search in the list of categories
              if (item.categories && item.categories.length > 0) {
                if (matchBool && item.categories) {
                  subMatches.push({
                    label: item.label,
                    value: item.value,
                    categories: item.categories
                  });
                } else {
                  categoryMatchedItems = item.categories.filter(section => {
                    const sectionKey = section[searchKey];
                    const lowerCaseSectionKey = sectionKey.toLowerCase();
                    if (
                      section &&
                      sectionKey &&
                      lowerCaseSectionKey.startsWith(lowerCaseTarget)
                    ) {
                      return section;
                    }
                    return false;
                  });

                  subMatches.push({
                    label: item.label,
                    value: item.value,
                    categories: categoryMatchedItems
                  });
                }
              }
            }
            return false;
          });
          return temp;
        });

        const categories = subMatches.filter(
          cat => cat.categories && cat.categories.length > 0
        );

        /**
         * If there are no categories, return the matches from the regular list
         * If there are categories and mathces inside of them, return the category name and its items
         * If the match is a category name, return the category and all its items
         * If there is no match, return 'No results found.'
         */
        if (categories && categories.length > 0) {
          newList = categories.flat();
          setItemsList(newList);
          setfocusableItemObjects(newList);
        } else if (matchedItems && matchedItems.length > 0) {
          newList = matchedItems.flat();
          setItemsList(newList);
          setfocusableItemObjects(newList);
        } else {
          newList = [{ label: i18n.noResultsLabel, value: 'no_results' }];
          setItemsList(newList);
          setfocusableItemObjects(newList);
        }
      } else {
        setItemsList(items);
        setfocusableItemObjects(newList);
      }
      return newList;
    }

    // When the items array is updated, reload the dropdown ScreenReaderMessage
    useEffect(() => {
      /*
        If the component does not have the active 'searchBehavior' flag, do not refresh the list live.
        If the dropdown is closed, there is no need to render the changes here, this will be handled
        when the dropdown is opened or the component is focused on.
        NOTE: type 'nested' dropdown is not currently supported for this
      */
      if (!(searchBehavior && type !== 'nested' && isDropdownOpen)) {
        return;
      }

      if (typeof inputValue.value === 'string') {
        setNavigationMode(false);
        setScreenReaderMessage(i18n.listUpdated);
        i18n.inputErrorText = '';

        const list = items;
        if (
          items.length &&
          typeof items[items.length - 1].value === 'string' &&
          items[items.length - 1].value === 'scrollbottomflag'
        ) {
          setScrollBottom(true);
          items.pop();
          return;
        }
        setItemsList(list);

        if (list && list.length > 0 && inputValue.value) {
          if (list[0].value === 'no_results') {
            // i18nProP.noMatchFound for backward compatability
            setScreenReaderMessage(
              i18nProp?.noMatchFound || i18n.noResultsLabel
            );
          } else if (!scrollBottom) {
            setScreenReaderMessage(
              ` ${i18n.newMatches} ${list.map(i =>
                i.categories ? i.categories.map(c => c.label) : i.label
              )}`
            );
          }
        }

        if (inputValue.value === '') {
          setScreenReaderMessage(i18n.noSelectionScreenReader);
          clearFocus(true);
        }
        setScrollBottom(false);
      }
    }, [JSON.stringify(items)]);

    // Change event on the input element.
    function handleInputChange(event) {
      setNavigationMode(false);
      setScreenReaderMessage('');
      i18n.inputErrorText = '';
      if (event && event.target) {
        showInputValue(true);
        const newValue = event.target.value;

        const list = handleInputSearch(newValue);

        if (list && list.length > 0 && newValue) {
          if (list[0].value === 'no_results') {
            // i18nProP.noMatchFound for backward compatability
            setScreenReaderMessage(
              i18nProp?.noMatchFound || i18n.noResultsLabel
            );
          } else {
            setScreenReaderMessage(
              ` ${i18n.newMatches} ${list.map(i =>
                i.categories ? i.categories.map(c => c.label) : i.label
              )}`
            );
          }
        }
        if (newValue !== '') {
          setDropdownOpenTo(true);
        }

        if (type === 'multi') {
          if (newValue === '') {
            navigateFocus(false, '', -1);
          } else {
            setFocusedItem({ index: -1, value: '' });
          }
        }

        if (type === 'single' && newValue === '') {
          setScreenReaderMessage(i18n.noSelectionScreenReader);
          clearFocus(true);
        }

        if (type === 'nested') {
          const newSelection = selectedItems.length ? [selectedItems[0]] : [];
          setSelection(newSelection);
        }
        setInputValue({ value: newValue, isMultiValue: false });

        if (inputProps && typeof inputProps.onInputChange === 'function') {
          inputProps.onInputChange(event);
        }
      }
    }

    const getActiveDescednant = () => {
      // catgegories break focusedItemObj.index, ^= querySelector is a workaround
      if (type === 'single') {
        return comboRef.current?.querySelector(
          `[id^="${kebabCase(inputValue.value)}-list-item-${
            selectedItems.length - 1
          }-"]`
        )?.id;
      }
      if (type === 'multi') {
        const listItemLabel =
          focusedItemObj.index > -1 && focusableItemObject[focusedItemObj.index]
            ? focusableItemObject[focusedItemObj.index].label
            : focusedItemObj.value;
        return comboRef.current?.querySelector(
          `[id^="${kebabCase(listItemLabel)}-list-item-0-"]`
        )?.id;
      }
      // type === nested
      return comboRef.current?.querySelector(
        `[id^="${kebabCase(
          selectedItems[selectedItems.length - 1]?.label
        )}-list-item-${selectedItems.length - 1}-"]`
      )?.id;
    };

    let inputIcon = 'search';
    if (!searchBehavior) {
      inputIcon = isDropdownOpen ? 'arrow-collapse' : 'arrow-expand';
    }

    const listMenuId = `${dataTest}Id`;

    return (
      <div
        css={theme => [
          styles({
            position,
            selectBehavior,
            disabled,
            theme,
            searchBehavior,
            isFullWidth
          }).menuContainer,
          customCSS(theme)
        ]}
        data-test={`${dataTest}-cybsCombobox`}
        className={className}
        ref={comboRef}
      >
        <div
          style={isDropdownOpen ? { zIndex: 10000, position: 'relative' } : {}}
        >
          <CybsTextField
            {...inputProps}
            aria-expanded={searchBehavior ? undefined : isDropdownOpen}
            aria-haspopup="listbox"
            aria-activedescendant={getActiveDescednant()}
            aria-controls={searchBehavior ? undefined : listMenuId}
            dataTest={dataTest}
            disabled={disabled}
            i18n={{
              errorText: i18n.inputErrorText,
              helpText: i18n.helpText,
              placeholder: inputProps.placeholder,
              label: i18n.label || (inputProps && inputProps.label),
              ariaLabel: i18n.ariaLabel || inputProps?.ariaLabel,
              tooltipLabel: isDropdownOpen ? 'Collapse' : 'Expand',
              buttonAriaLabel: isDropdownOpen
                ? i18n.collapsedIcon
                : i18n.expandedIcon
            }}
            iconClass={inputIcon}
            isInvalid={isInputInvalid}
            noValidate
            onBlur={handleInputBlur}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onClick={handleInputClick}
            onIconClick={searchBehavior ? undefined : handleInputClick}
            onFocus={handleInputFocus}
            readOnly={readOnly}
            css={theme =>
              styles({
                position,
                selectBehavior,
                disabled,
                theme,
                searchBehavior,
                isFullWidth
              }).inputContainer
            }
            useNativePlaceholder={useNativePlaceholder}
            prenventIconFocus
            showErrorText={inputProps.showErrorText}
            value={isValueVisible ? inputValue.value : ''}
            width={isFullWidth ? '100%' : width}
            role={searchBehavior ? undefined : 'combobox'}
            ref={inputRef}
            required={required}
            aria-required={required || undefined}
            hideIconLabel
            type={searchBehavior ? 'search' : undefined}
          />
        </div>
        {isDropdownOpen && itemsList ? (
          <div
            css={styles({ position, isFullWidth }).menuWrapper}
            style={{ zIndex: 10000 }}
          >
            <CybsMenu
              id={listMenuId}
              dataTest={dataTest}
              enableListboxKeydown={false}
              focusedIndex={focusedItemObj.index}
              focusedValue={focusedItemObj.value}
              onListSelect={handleItemClick}
              onListScrollBottom={checkSearchBehaviour}
              optionsTemplate={optionsTemplate}
              openNestedValue={openNestedDropdown}
              ref={menuRef}
              scrollOnLoad={Boolean(!tableId)}
              selectedItems={selectedItems}
              show={isDropdownOpen}
              type={type}
              width={width}
              lang={lang}
              useLowResolutionStyles={showBackButton}
              i18n={i18n}
            >
              {itemsList}
            </CybsMenu>
          </div>
        ) : null}
        <div
          aria-live="assertive"
          style={{
            position: 'absolute',
            left: '-10000px',
            top: 'auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden'
          }}
        >
          {screenReaderMessage}
        </div>
      </div>
    );
  }
);

CybsCombobox.propTypes = {
  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * if to automatically set focus to the input field on FIRST render only.
   */
  autofocus: PropTypes.bool,

  /**
   * dataTest string for testing.
   */
  dataTest: PropTypes.string.isRequired,

  /**
   *If `true`, the combobox  is not interactive.
   */
  disabled: PropTypes.bool,

  /**
   * lang tag for localization
   */
  lang: PropTypes.string,

  /**
   * Localization
   */
  i18n: PropTypes.shape({
    /**
     * Input error text.
     */
    inputErrorText: PropTypes.string,
    /**
     * Label to indicate the number of selected items.
     */
    itemsSelectedLabel: PropTypes.string,
    /**
     * Label to indicate the number of selected items.
     */
    collapsedIcon: PropTypes.string,

    /**
     * Placeholder for the input.
     */
    expandedIcon: PropTypes.string,

    /**
     * Text displayed under the input field.
     */
    helpText: PropTypes.string,

    /**
     * Label on top of the input.
     */
    label: PropTypes.string,

    /**
     * Aria-label on top of the input.
     */
    ariaLabel: PropTypes.string,

    /**
     * Label to display when new results are available.
     */
    listUpdated: PropTypes.string,

    /**
     * Label on top of the input.
     */
    newMatches: PropTypes.string,

    /**
     * Label on top of the input.
     */
    noMatchFound: PropTypes.string,

    /**
     * Label to display if there are no results that match the user's search criteria.
     */
    noSelectionScreenReader: PropTypes.string,

    /**
     * Label to display if there are no results that match the user's search criteria.
     */
    noResultsLabel: PropTypes.string,

    /**
     * Label to display on focus when searchBehavior is present.
     */
    searchBehavior: PropTypes.string
  }),

  /**
   * to check if there is an error
   */
  isInputInvalid: PropTypes.bool,
  /**
   * Props to applied to the input element.
   */
  inputProps: PropTypes.shape({
    /**
     * Placeholder for the input.
     */
    placeholder: PropTypes.string,

    /**
     * Label on top of the input.
     */
    label: PropTypes.string,

    /**
     * Callback fired on blur on the input.
     * @deprecated
     */
    onInputBlur: PropTypes.func,

    /**
     * Callback fired when the input value changes.
     */
    onInputChange: PropTypes.func,

    /**
     * Callback fired when the input element is clicked.
     */
    onInputClick: PropTypes.func,

    /**
     * Callback fired on focus on the input.
     */
    onInputFocus: PropTypes.func,

    /**
     * Callback fired on key down in the input element.
     */
    onInputKeyDown: PropTypes.func,

    /**
     * If `true` the input turns red and the error mesage is visible.
     */
    showErrorText: PropTypes.bool
  }),

  /**
   * optionsTemplate to render multiple attributes in the listbox
   */
  optionsTemplate: PropTypes.shape({
    optionValues: PropTypes.arrayOf(
      PropTypes.shape({ value: PropTypes.string.isRequired })
    ),
    renderOption: PropTypes.func.isRequired,
    renderLabel: PropTypes.func.isRequired,
    validationTemplate: PropTypes.func.isRequired
  }),

  /**
   * If `true`, the width will be the full width of its parent container.
   */
  isFullWidth: PropTypes.bool,

  /**
   * @deprecated
   *If `true`, the dropdown is open.
   */
  isOpen: PropTypes.bool,

  /**
   * List of items in the listbox.
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })
  ).isRequired,

  /**
   * Custom Filter Function to pass down into combobox
   */
  filterFunction: PropTypes.func,

  /**
   * Callback fired when the dropdown isOpen is set to false
   */
  onClose: PropTypes.func,

  /**
   * Callback fired when the the user tabs/clicks away from the combobox.
   */
  onBlur: PropTypes.func,

  /**
   * Callback fired when an item is selected.
   */
  onSelect: PropTypes.func,

  /**
   * Callback fired when scroll reaches bottom of scrollable list
   */
  onScrollBottom: PropTypes.func,
  /**
   *If `true`, the combobox might have a prefilled value and will receive focus but  is not interactive.
   */
  readOnly: PropTypes.bool,

  /**
   * Different positions for the dropdown around the input element.
   */
  position: PropTypes.shape({
    horizontal: PropTypes.oneOf(['left', 'center', 'right']),
    vertical: PropTypes.oneOf(['bottom', 'top'])
  }),

  /**
   * Key to use to perform the search.
   */
  searchKey: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string
  ]),

  /**
   * Selected item(s).
   */
  selectedItems: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string
      })
    ),
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })
  ]),

  /**
   * @ignore
   */
  tableId: PropTypes.string,

  /**
   * Different type of dropdowns.
   */
  type: PropTypes.oneOf(['multi', 'single', 'nested']),

  /**
   * @deprecated
   * If `true`, the input will be valid upon selection.
   */
  validateOnSelection: PropTypes.bool,

  /**
   * Width of the input and dropdown menu in px or rem.
   */
  width: PropTypes.string,

  /**
   * @ignore
   */
  searchBehavior: PropTypes.bool,

  /**
   * @ignore
   */
  selectBehavior: PropTypes.bool,
  /**
   * Styling function to allow CSS overrides via Emotion
   *
   * @ignore
   */
  customCSS: PropTypes.func,
  /**
   * @ignore
   */
  required: PropTypes.bool,
  /**
   * @ignore
   */
  useNativePlaceholder: PropTypes.bool,
  /**
   * Used to have nested listbox be one column rather than multi
   */
  compactListMode: PropTypes.bool
};

CybsCombobox.defaultProps = {
  className: '',
  disabled: false,
  autofocus: false,
  lang: 'en',
  i18n: {
    collapsedIcon: 'Collapse',
    expandedIcon: 'Expand',
    inputErrorText: '',
    itemsSelectedLabel: 'items selected',
    label: '',
    ariaLabel: '',
    helpText: '',
    listUpdated: 'List Updated.',
    newMatches: 'New matches below.',
    noMatchFound: 'No match found.',
    noSelectionScreenReader: 'No Selection',
    noResultsLabel: 'No results found.',
    noResutsLabel: 'No results found.',
    searchBehavior: 'Typing changes listbox contents.'
  },
  isFullWidth: false,
  isInputInvalid: false,
  inputProps: {
    label: '',
    placeholder: 'Select an option',
    showErrorText: true
  },
  isOpen: false,
  onClose: () => {},
  onSelect: () => {},
  onScrollBottom: () => {},
  onBlur: undefined,
  filterFunction: null,
  readOnly: false,
  position: {
    horizontal: 'left',
    vertical: 'bottom'
  },
  searchKey: 'label',
  selectedItems: [],
  tableId: '',
  type: 'single',
  validateOnSelection: true,
  width: '',
  optionsTemplate: undefined,
  searchBehavior: false,
  selectBehavior: false,
  customCSS: () => {},
  required: false,
  useNativePlaceholder: true,
  compactListMode: false
};

CybsCombobox.displayName = 'CybsCombobox';

export default CybsCombobox;
